'use client'

import { Main } from '@/components/layout/main'
import { PageHeader } from '@/components/layout/page-header'
import { useTranslation } from 'react-i18next'
import { useEffect, useState, useCallback } from 'react'
import { useRouter, useSearch } from '@tanstack/react-router'
import { filterNonNullValues } from '@/lib/utils'
import { DataTable } from '@/components/table'
import DataTableSkeleton from '@/components/ui/data-table-skeleton'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import { PlusCircle, UserX } from 'lucide-react'
import {
    CaseAdversary,
    AdversaryTableParams,
    get_adversaries
} from '@/api/case'
import AdversariesProvider, { useAdversaries } from './context/adversary-context'
import { useAdversaryColumns } from './components/adversaries-columns'
import { AdversariesDialogs } from './components/adversaries-dialogs'
import { DataTableToolbar } from './components/data-table-toolbar'
import { PermissionGuard } from '@/components/permission-guard'
import { UserPermission } from '@/constants/permissions'

function AdversariesContent() {
    const { t } = useTranslation()
    const search = useSearch({ strict: false })
    const { setOpen } = useAdversaries()

    const [adversaries, setAdversaries] = useState<CaseAdversary[]>([])
    const [loading, setLoading] = useState<boolean>(true)
    const [error, setError] = useState<boolean>(false)
    const [pagesCnt, setPagesCnt] = useState<number>(0)

    const [params, setParams] = useState<AdversaryTableParams>({
        page: search?.page || 0,
        page_size: search?.page_size || 10,
        ordering: search?.ordering || null,
        search: search?.search || null,
        client_type: search?.client_type || null,
        id_type: search?.id_type || null,
    })

    const router = useRouter()

    // Sync params to router search params
    useEffect(() => {
        router.navigate({
            to: ".",
            search: filterNonNullValues(params as any),
            replace: true,
        })
    }, [params, router])

    const fetchAdversaries = useCallback(() => {
        setLoading(true)
        get_adversaries(params)
            .then((resp) => {
                setAdversaries(resp.results)
                setPagesCnt(Math.ceil(resp.count / params.page_size))
                setLoading(false)
                setError(false)
            })
            .catch((_) => {
                setError(true)
                setLoading(false)
            })
    }, [params])

    useEffect(() => {
        fetchAdversaries()
    }, [fetchAdversaries])

    const columns = useAdversaryColumns()

    const handleRowClick = (row: CaseAdversary) => {
        // Navigate to adversary details using the client ID (the party ID)
        if (row.client?.id) {
            router.navigate({ to: `/adversaries/${row.client.id}` })
        }
    }

    return (
        <Main>
            <PageHeader
                leading={
                    <span className="mt-1 inline-flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                        <UserX className="h-5 w-5" />
                    </span>
                }
                title={t('adversaries')}
                description={t('adversaries_desc')}
                actions={
                    loading ? (
                        <Skeleton className="h-9 w-32" />
                    ) : (
                        <PermissionGuard permission={UserPermission.ADD_CASE_PARTY}>
                            <Button onClick={() => setOpen('add')}>
                                <PlusCircle className="h-4 w-4 me-2" />
                                {t('add_adversary')}
                            </Button>
                        </PermissionGuard>
                    )
                }
            />

            <div className='-mx-4 flex-1 overflow-auto px-4 py-1 lg:flex-row lg:space-x-12 lg:space-y-0'>
                {loading ? (
                    <DataTableSkeleton columns={7} rows={5} />
                ) : error ? (
                    <div className='py-8 text-center text-red-500'>
                        {t('table_load_error')}
                    </div>
                ) : (
                    <DataTable
                        data={adversaries}
                        columns={columns}
                        params={params}
                        setParams={setParams}
                        pages={pagesCnt}
                        ToolbarName={DataTableToolbar}
                        storageKey="adversaries-table"
                        onRowClick={handleRowClick}
                    />
                )}
            </div>

            <AdversariesDialogs onUpdate={fetchAdversaries} />
        </Main>
    )
}

export default function Adversaries() {
    return (
        <AdversariesProvider>
            <AdversariesContent />
        </AdversariesProvider>
    )
}
